import { paymentDraftSchema, paymentMoneySchema } from "@ai-crm/shared";
import type {
  AdminPaymentDTO,
  PaymentAllocationDTO,
  PaymentDraft,
  PaymentActualPayer,
} from "@ai-crm/shared";
import { useId } from "react";
import {
  formatPaymentRub,
  isoToMoscowInput,
  moscowDateTimeToIso,
} from "../../lib/format";

export interface PaymentRecordValues {
  amountRub: string;
  receivedAt: string;
  identityKind: "BANK_OPERATION" | "STATEMENT_ROW";
  operationId: string;
  statementId: string;
  rowId: string;
  payerName: string;
  payerInn: string;
  note: string;
}
export function initialPaymentRecord(
  payer: PaymentActualPayer,
  amount = "",
  payment?: AdminPaymentDTO,
): PaymentRecordValues {
  return {
    amountRub: payment?.amountRub ?? amount,
    receivedAt: payment ? isoToMoscowInput(payment.receivedAt) : "",
    identityKind: payment?.bankIdentity.kind ?? "BANK_OPERATION",
    operationId:
      payment?.bankIdentity.kind === "BANK_OPERATION"
        ? payment.bankIdentity.operationId
        : "",
    statementId:
      payment?.bankIdentity.kind === "STATEMENT_ROW"
        ? payment.bankIdentity.statementId
        : "",
    rowId:
      payment?.bankIdentity.kind === "STATEMENT_ROW"
        ? payment.bankIdentity.rowId
        : "",
    payerName: payer.name,
    payerInn: payer.inn ?? "",
    note: payment?.note ?? "",
  };
}
export function parsePaymentRecord(value: PaymentRecordValues): PaymentDraft {
  const receivedAt = moscowDateTimeToIso(value.receivedAt);
  if (!receivedAt)
    throw new Error(
      "Укажите действительную дату и время поступления по Москве.",
    );
  const parsed = paymentDraftSchema.safeParse({
    amountRub: value.amountRub.trim().replace(",", "."),
    receivedAt,
    currency: "RUB",
    bankIdentity:
      value.identityKind === "BANK_OPERATION"
        ? { kind: "BANK_OPERATION", operationId: value.operationId }
        : {
            kind: "STATEMENT_ROW",
            statementId: value.statementId,
            rowId: value.rowId,
          },
    payer: { name: value.payerName, inn: value.payerInn.trim() || null },
    note: value.note.trim() || null,
  });
  if (!parsed.success)
    throw new Error(
      `Проверьте банковские данные: ${parsed.error.issues.map((i) => i.path.join(".")).join(", ")}`,
    );
  return parsed.data;
}
export function PaymentRecordForm({
  value,
  onChange,
  disabled = false,
}: {
  value: PaymentRecordValues;
  onChange: (next: PaymentRecordValues) => void;
  disabled?: boolean;
}) {
  const prefix = useId();
  const field = (
    key: keyof PaymentRecordValues,
    label: string,
    type = "text",
    inputMode?: "decimal" | "numeric",
  ) => (
    <label className="block space-y-1 text-sm" htmlFor={`${prefix}-${key}`}>
      <span>{label}</span>
      <input
        id={`${prefix}-${key}`}
        className="control"
        type={type}
        inputMode={inputMode}
        step={type === "datetime-local" ? "0.001" : undefined}
        value={value[key]}
        disabled={disabled}
        onChange={(e) => onChange({ ...value, [key]: e.target.value })}
      />
    </label>
  );
  return (
    <fieldset disabled={disabled} className="space-y-3">
      <legend className="font-medium">Данные банковского поступления</legend>
      <p className="text-sm text-muted-foreground">
        Валюта: RUB. Сверьте плательщика и дату с выпиской; это время
        поступления в банке, а не обработки в админке.
      </p>
      <div className="grid gap-3 sm:grid-cols-2">
        {field("amountRub", "Сумма поступления, ₽", "text", "decimal")}
        {field("receivedAt", "Поступило в банке (МСК)", "datetime-local")}
      </div>
      <label className="block space-y-1 text-sm">
        <span>Идентификатор из банка</span>
        <select
          className="control"
          value={value.identityKind}
          onChange={(e) =>
            onChange({
              ...value,
              identityKind: e.target
                .value as PaymentRecordValues["identityKind"],
            })
          }
        >
          <option value="BANK_OPERATION">ID банковской операции</option>
          <option value="STATEMENT_ROW">Строка банковской выписки</option>
        </select>
      </label>
      {value.identityKind === "BANK_OPERATION" ? (
        field("operationId", "ID операции")
      ) : (
        <div className="grid gap-3 sm:grid-cols-2">
          {field("statementId", "ID выписки")}
          {field("rowId", "ID строки")}
        </div>
      )}
      <div className="grid gap-3 sm:grid-cols-2">
        {field("payerName", "Фактический плательщик")}
        {field(
          "payerInn",
          "ИНН плательщика (если известен)",
          "text",
          "numeric",
        )}
      </div>
      <label className="block space-y-1 text-sm">
        <span>Примечание к поступлению</span>
        <textarea
          className="control min-h-20"
          value={value.note}
          onChange={(e) => onChange({ ...value, note: e.target.value })}
        />
      </label>
    </fieldset>
  );
}
export function readAllocations(
  values: Record<string, string>,
): PaymentAllocationDTO[] {
  return Object.entries(values)
    .filter(([, amount]) => amount.trim() !== "")
    .map(([paymentId, amount]) => {
      const parsed = paymentMoneySchema.safeParse(
        amount.trim().replace(",", "."),
      );
      if (!parsed.success)
        throw new Error(
          "Введите положительную сумму зачёта с точностью до копейки.",
        );
      return { paymentId, amountRub: parsed.data };
    });
}
export function PaymentAllocationFields({
  payments,
  values,
  onChange,
  disabled = false,
}: {
  payments: AdminPaymentDTO[];
  values: Record<string, string>;
  onChange: (next: Record<string, string>) => void;
  disabled?: boolean;
}) {
  return (
    <fieldset disabled={disabled} className="space-y-2">
      <legend className="font-medium">Зачёт поступлений</legend>
      <p className="text-sm text-muted-foreground">
        Укажите сумму из каждого перевода. Итог и допустимый остаток проверит
        сервер.
      </p>
      {payments
        .filter((p) => p.recordState === "VALID" && p.unallocatedRub !== "0")
        .map((p) => (
          <label key={p.id} className="block space-y-1 text-sm">
            <span className="break-all">
              Перевод {p.id} · {formatPaymentRub(p.amountRub)} · не зачтено{" "}
              {formatPaymentRub(p.unallocatedRub)}
            </span>
            <input
              className="control"
              aria-label={`Зачесть из ${p.id}`}
              inputMode="decimal"
              value={values[p.id] ?? ""}
              onChange={(e) => onChange({ ...values, [p.id]: e.target.value })}
            />
          </label>
        ))}
    </fieldset>
  );
}
